/* @mentions in team notes (Task-Management Phase 1 / T3).
 *
 * DESIGN-APPROVED ruling: docs/superpowers/plans/2026-07-10-task-mgmt-d4-d7-
 * rulings.md §D4. Two halves live here:
 *
 *  1. RENDER — a note body is parsed into an ordered parts array (text
 *     segments + mention tokens) and each mention becomes a styled chip. The
 *     chip label is a LIT TEXT CHILD (`${p.label}`) — auto-escaped exactly
 *     like `${n.body}` renders a plain note today — never innerHTML /
 *     unsafeHTML. The id capture is `\d+` only, so nothing but digits ever
 *     reaches the DOM there. A hostile label like `<img src=x onerror=...>`
 *     therefore renders as inert text (proved in mentions.test.ts).
 *
 *  2. ENTRY — typing `@` in a note composer opens a roster popover sourced
 *     from `ws.users` (the Phase 0 `GET /users` roster, already fetched in
 *     WS.init()). Picking a user inserts a canonical, id-bearing token
 *     `@[Display Name](u:42)` — NOTHING freehand becomes a mention; the
 *     server re-derives the actual mentions from the stored body by numeric
 *     id (workspace.py parse_mentions), so the label is display-only and name
 *     collisions are impossible.
 *
 * The popover reuses the ui/popover.ts PATTERN (a body-appended element,
 * document-anchored, lit-rendered, closed on outside interaction) rather than
 * that function itself — ui/popover.ts is the bespoke IMPACT-result popover,
 * not a generic control. Kept deliberately small; flagged for the design pass.
 *
 * ACCESSIBILITY (T3): the entry popover implements the ARIA combobox/listbox
 * pattern so screen readers announce it — the popover is role="listbox" with
 * role="option" children (aria-selected tracks the highlight), and the bound
 * composer textarea becomes a role="combobox" (aria-expanded / aria-controls /
 * aria-autocomplete="list" / aria-activedescendant) while open, reverting to a
 * plain textarea on close. Covered by mentions.test.ts.
 */
import { html, nothing, render, type TemplateResult } from 'lit';

// Same shape the server resolves against (workspace.py `_MENTION_RE`): a
// markdown-link-shaped token whose id is digits only.
export const MENTION_RE = /@\[([^\]]+)\]\(u:(\d+)\)/g;

export interface MentionPart {
  t: 'text' | 'mention';
  /** text content (t==='text') OR the display label (t==='mention') */
  v: string;
  /** resolved user id, present only when t==='mention' */
  id?: number;
}

interface RosterUser {
  id: number;
  name: string;
  email?: string;
}

/** Split a note body into an ordered array of text/mention parts. */
export function mentionParts(body: string): MentionPart[] {
  const parts: MentionPart[] = [];
  const re = new RegExp(MENTION_RE.source, 'g');
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(body)) !== null) {
    if (m.index > last) parts.push({ t: 'text', v: body.slice(last, m.index) });
    parts.push({ t: 'mention', v: m[1] ?? '', id: Number(m[2]) }); // both groups always capture
    last = m.index + m[0].length;
  }
  if (last < body.length) parts.push({ t: 'text', v: body.slice(last) });
  return parts;
}

/* Render a note body with @mention tokens as inert, styled chips. Returns a
 * lit-interpolatable array (plain strings render as auto-escaped text; the
 * chip's `${p.v}` label is likewise an auto-escaped text child). Drop-in for
 * a former `${n.body}` binding. */
export function renderMentionBody(body: string): Array<string | TemplateResult> {
  return mentionParts(body).map((p) =>
    p.t === 'mention'
      ? html`<span class="mention"><span class="at">@</span>${p.v}</span>`
      : p.v,
  );
}

/* ── the `@`-autocomplete controller (a body-level singleton popover) ───── */

let pop: HTMLDivElement | null = null;
let popTa: HTMLTextAreaElement | null = null;
let popItems: RosterUser[] = [];
let popActive = 0;
let popStart = 0; // index of the `@` that opened the current query

/* The active `@query` immediately before the caret, or null. Requires the `@`
 * to sit at the start or after whitespace (so an email's `a@b` never triggers)
 * and the query to carry none of the token's own delimiters. Exported for the
 * unit test. */
export function mentionQueryAt(
  value: string,
  caret: number,
): { start: number; query: string } | null {
  const before = value.slice(0, caret);
  const m = before.match(/(?:^|\s)@([^\s@[\]()]*)$/);
  if (!m) return null;
  const query = m[1] ?? ''; // group 1 always captures (possibly empty)
  return { start: caret - query.length - 1, query };
}

/** name/email substring match, capped for a tidy popover. Exported for tests. */
export function filterRoster(users: readonly RosterUser[], query: string): RosterUser[] {
  const q = query.toLowerCase();
  return (users || [])
    .filter(
      (u) =>
        !q ||
        u.name.toLowerCase().includes(q) ||
        (u.email || '').toLowerCase().includes(q),
    )
    .slice(0, 8);
}

// Stable dom id for the active option, so the composer's textarea can point
// aria-activedescendant at it (ARIA combobox/listbox pattern, T3).
const OPT_ID = (i: number) => `mentionpop-opt-${i}`;

function renderPop(): void {
  if (!pop) return;
  // The popover IS the listbox; label it for screen readers.
  pop.setAttribute('role', 'listbox');
  pop.setAttribute('aria-label', 'Team members to mention');
  render(
    html`${popItems.map(
      (u, i) => html`
        <button
          type="button"
          id=${OPT_ID(i)}
          role="option"
          aria-selected=${i === popActive ? 'true' : 'false'}
          class="mp-item ${i === popActive ? 'active' : ''}"
          @mousedown=${(e: MouseEvent) => {
            e.preventDefault(); // keep the textarea focused through the pick
            pick(u);
          }}
        >
          <span class="mp-name">${u.name}</span>${u.email
            ? html`<span class="mp-email">${u.email}</span>`
            : nothing}
        </button>
      `,
    )}`,
    pop,
  );
  // Keep the combobox's active descendant in sync with the highlighted option
  // (updated here so arrow-key navigation, which re-renders, moves it too).
  if (popTa) popTa.setAttribute('aria-activedescendant', OPT_ID(popActive));
}

// Turn the bound composer textarea into an ARIA combobox while the popover is
// open (role + expanded + controls + autocomplete), and revert it to a plain
// textarea on close so its semantics are unchanged when not mentioning.
function setComboboxOpen(ta: HTMLTextAreaElement): void {
  ta.setAttribute('role', 'combobox');
  ta.setAttribute('aria-autocomplete', 'list');
  ta.setAttribute('aria-haspopup', 'listbox');
  ta.setAttribute('aria-expanded', 'true');
  ta.setAttribute('aria-controls', 'mentionpop');
}
function clearCombobox(ta: HTMLTextAreaElement): void {
  for (const a of [
    'role', 'aria-autocomplete', 'aria-haspopup', 'aria-expanded',
    'aria-controls', 'aria-activedescendant',
  ]) {
    ta.removeAttribute(a);
  }
}

function position(ta: HTMLTextAreaElement): void {
  if (!pop) return;
  const r = ta.getBoundingClientRect();
  pop.style.left = r.left + window.scrollX + 'px';
  pop.style.top = r.bottom + window.scrollY + 'px';
  pop.style.minWidth = Math.max(180, r.width) + 'px';
}

function openPop(ta: HTMLTextAreaElement): void {
  if (!pop) {
    pop = document.createElement('div');
    pop.className = 'mentionpop';
    pop.id = 'mentionpop';
    document.body.appendChild(pop);
  }
  position(ta);
  setComboboxOpen(ta);
  renderPop();
}

function closePop(): void {
  if (popTa) clearCombobox(popTa);
  if (pop) {
    pop.remove();
    pop = null;
  }
  popTa = null;
  popItems = [];
  popActive = 0;
}

function pick(u: RosterUser): void {
  const ta = popTa;
  if (!ta) {
    closePop();
    return;
  }
  const caret = ta.selectionStart ?? ta.value.length;
  // `]` is the token's own delimiter (server regex `[^\]]+`); strip both
  // brackets from the label so the inserted token always parses.
  const label = u.name.replace(/[[\]]/g, '');
  const token = `@[${label}](u:${u.id}) `;
  ta.value = ta.value.slice(0, popStart) + token + ta.value.slice(caret);
  const posn = popStart + token.length;
  ta.setSelectionRange(posn, posn);
  ta.focus();
  ta.dispatchEvent(new Event('input', { bubbles: true }));
  closePop();
}

/* Bind these three to a composer textarea (`@input`, `@keydown`, `@blur`). */
export function mentionInput(ev: Event, ws: { users?: readonly RosterUser[] }): void {
  const ta = ev.target as HTMLTextAreaElement;
  const q = mentionQueryAt(ta.value, ta.selectionStart ?? ta.value.length);
  if (!q) {
    closePop();
    return;
  }
  const items = filterRoster(ws.users || [], q.query);
  if (!items.length) {
    closePop();
    return;
  }
  popTa = ta;
  popStart = q.start;
  popItems = items;
  popActive = 0;
  openPop(ta);
}

export function mentionKeydown(ev: KeyboardEvent): void {
  if (!pop) return;
  if (ev.key === 'ArrowDown') {
    popActive = (popActive + 1) % popItems.length;
    renderPop();
    ev.preventDefault();
  } else if (ev.key === 'ArrowUp') {
    popActive = (popActive - 1 + popItems.length) % popItems.length;
    renderPop();
    ev.preventDefault();
  } else if (ev.key === 'Enter') {
    const it = popItems[popActive];
    if (it) pick(it); // popover only opens with a non-empty roster
    ev.preventDefault();
  } else if (ev.key === 'Escape') {
    closePop();
    ev.preventDefault();
  }
}

export function mentionBlur(): void {
  // defer close so a click/mousedown on a roster item still lands
  setTimeout(closePop, 120);
}
